import './FlowPetRPG.css';

type TimerStatus = 'idle' | 'running' | 'paused' | 'break' | 'completed' | 'neglect';
type SessionLength = 25 | 50;
type PetStage = 'egg' | 'hatchling' | 'juvenile' | 'final';
type EquipmentSlot = 'head' | 'body' | 'hand';

type CostumeItem = {
  id: string;
  name: string;
  slot: EquipmentSlot;
  description: string;
  glyph: string;
  unlockLevel: number;
};

type AppState = {
  timerStatus: TimerStatus;
  selectedMinutes: SessionLength;
  remainingMs: number;
  sessionDurationMs: number;
  creditedFocusMs: number;
  level: number;
  xp: number;
  health: number;
  inventory: string[];
  equipped: Partial<Record<EquipmentSlot, string>>;
  openedMilestones: number[];
  currentPetType: PetStage;
  lastWarningAt: number | null;
  message: string;
};

type WorkerEvent = {
  type: 'tick' | 'complete';
  mode: 'focus' | 'break';
  remainingMs: number;
  elapsedMs: number;
  durationMs: number;
  now: number;
};

const storageKey = 'flow-pet-rpg-state-v1';
const minuteMs = 60 * 1000;
const healthPenalty = 12;
const xpPenalty = 25;
const neglectGraceMs = 30 * 1000;
const lootMilestones = [3, 7, 12, 20, 30];

const items: CostumeItem[] = [
  {
    id: 'tiny-wizard-hat',
    name: 'Tiny wizard hat',
    slot: 'head',
    description: '+Mystique for serious little scholars.',
    glyph: '^',
    unlockLevel: 3,
  },
  {
    id: 'wooden-sword',
    name: 'Wooden sword',
    slot: 'hand',
    description: 'A brave starter blade for focus quests.',
    glyph: '/',
    unlockLevel: 7,
  },
  {
    id: 'iron-armor-vest',
    name: 'Iron armor vest',
    slot: 'body',
    description: 'Polished defense against distraction.',
    glyph: '[]',
    unlockLevel: 12,
  },
  {
    id: 'gold-focus-crown',
    name: 'Gold focus crown',
    slot: 'head',
    description: 'A gleaming reward for durable attention.',
    glyph: 'W',
    unlockLevel: 20,
  },
  {
    id: 'starforged-lance',
    name: 'Starforged lance',
    slot: 'hand',
    description: 'For the final form of a steady companion.',
    glyph: '|>',
    unlockLevel: 30,
  },
];

function createDefaultState(): AppState {
  return {
    timerStatus: 'idle',
    selectedMinutes: 25,
    remainingMs: 25 * minuteMs,
    sessionDurationMs: 25 * minuteMs,
    creditedFocusMs: 0,
    level: 1,
    xp: 0,
    health: 88,
    inventory: [],
    equipped: {},
    openedMilestones: [],
    currentPetType: 'egg',
    lastWarningAt: null,
    message: 'Tap the egg, choose a quest, and protect your focus streak.',
  };
}

function getXpToNext(level: number) {
  return 90 + level * 35;
}

function getStage(level: number): PetStage {
  if (level >= 30) return 'final';
  if (level >= 15) return 'juvenile';
  if (level >= 5) return 'hatchling';
  return 'egg';
}

function getStageName(stage: PetStage) {
  return {
    egg: 'Interactive Egg',
    hatchling: 'Hatchling',
    juvenile: 'Juvenile',
    final: 'Final Evolution',
  }[stage];
}

function loadState(): AppState {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return createDefaultState();
    const parsed = JSON.parse(raw) as Partial<AppState>;
    const fallback = createDefaultState();
    const selectedMinutes = parsed.selectedMinutes === 50 ? 50 : 25;
    const level = Math.max(1, Number(parsed.level) || 1);

    return {
      ...fallback,
      ...parsed,
      selectedMinutes,
      sessionDurationMs: selectedMinutes * minuteMs,
      remainingMs:
        typeof parsed.remainingMs === 'number' && parsed.remainingMs > 0
          ? parsed.remainingMs
          : selectedMinutes * minuteMs,
      level,
      xp: Math.max(0, Number(parsed.xp) || 0),
      health: Math.min(100, Math.max(0, Number(parsed.health) || fallback.health)),
      inventory: Array.isArray(parsed.inventory) ? parsed.inventory : [],
      equipped: parsed.equipped ?? {},
      openedMilestones: Array.isArray(parsed.openedMilestones) ? parsed.openedMilestones : [],
      currentPetType: getStage(level),
      timerStatus: parsed.timerStatus === 'running' || parsed.timerStatus === 'paused' ? 'paused' : parsed.timerStatus ?? 'idle',
    };
  } catch {
    return createDefaultState();
  }
}

function saveState(state: AppState) {
  window.localStorage.setItem(storageKey, JSON.stringify(state));
}

function formatTime(ms: number) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function make<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

export function initFlowPetRPG(root: HTMLElement) {
  let state = loadState();
  let worker: Worker | null = null;
  let hiddenStartedAt: number | null = null;
  let lastCreditTickAt = Date.now();
  let particleTimer: number | null = null;

  root.className = 'flow-pet-shell';
  root.innerHTML = `
    <section class="flow-pet-app" aria-label="Flow Pet RPG dashboard">
      <div class="flow-ambient" aria-hidden="true"></div>
      <section class="sanctuary-panel" aria-labelledby="sanctuary-title">
        <div class="panel-kicker">The Sanctuary</div>
        <div class="title-row">
          <div>
            <h2 id="sanctuary-title">Flow Pet RPG</h2>
            <p class="stage-name"></p>
          </div>
          <div class="level-badge" aria-label="Pet level"></div>
        </div>
        <div class="pet-stage" role="button" tabindex="0" aria-label="Interact with pet">
          <div class="particle-field" aria-hidden="true"></div>
          <div class="pet-composite" aria-live="polite"></div>
        </div>
        <div class="vitals-grid">
          <div class="meter-card">
            <span>Experience</span>
            <strong class="xp-copy"></strong>
            <div class="meter"><div class="xp-fill"></div></div>
          </div>
          <div class="meter-card">
            <span>Health</span>
            <strong class="health-copy"></strong>
            <div class="meter health-meter"><div class="health-fill"></div></div>
          </div>
        </div>
        <div class="sanctuary-actions">
          <button class="feed-button" type="button">Feed</button>
          <button class="play-button" type="button">Play</button>
        </div>
        <div class="inventory-panel">
          <div class="section-heading">
            <h2>Equipment Manager</h2>
            <span class="loot-copy"></span>
          </div>
          <div class="inventory-list"></div>
        </div>
      </section>

      <section class="portal-panel" aria-labelledby="portal-title">
        <div class="panel-kicker">The Focus Portal</div>
        <h2 id="portal-title">Begin a focus quest</h2>
        <div class="timer-switch" role="group" aria-label="Timer length">
          <button class="length-button" data-minutes="25" type="button">25 min</button>
          <button class="length-button" data-minutes="50" type="button">50 min</button>
        </div>
        <div class="countdown-card">
          <span class="status-copy"></span>
          <strong class="countdown"></strong>
          <p class="credit-copy"></p>
        </div>
        <div class="portal-actions">
          <button class="begin-button" type="button">Begin Quest</button>
          <button class="rest-button" type="button">Rest</button>
        </div>
        <div class="warning-card" role="status"></div>
        <div class="quest-log">
          <h3>Quest Log</h3>
          <p class="message-copy"></p>
        </div>
      </section>
    </section>
  `;

  const selectors = {
    stageName: root.querySelector<HTMLElement>('.stage-name')!,
    levelBadge: root.querySelector<HTMLElement>('.level-badge')!,
    petStage: root.querySelector<HTMLElement>('.pet-stage')!,
    petComposite: root.querySelector<HTMLElement>('.pet-composite')!,
    particleField: root.querySelector<HTMLElement>('.particle-field')!,
    xpCopy: root.querySelector<HTMLElement>('.xp-copy')!,
    xpFill: root.querySelector<HTMLElement>('.xp-fill')!,
    healthCopy: root.querySelector<HTMLElement>('.health-copy')!,
    healthFill: root.querySelector<HTMLElement>('.health-fill')!,
    feedButton: root.querySelector<HTMLButtonElement>('.feed-button')!,
    playButton: root.querySelector<HTMLButtonElement>('.play-button')!,
    inventoryList: root.querySelector<HTMLElement>('.inventory-list')!,
    lootCopy: root.querySelector<HTMLElement>('.loot-copy')!,
    lengthButtons: Array.from(root.querySelectorAll<HTMLButtonElement>('.length-button')),
    statusCopy: root.querySelector<HTMLElement>('.status-copy')!,
    countdown: root.querySelector<HTMLElement>('.countdown')!,
    creditCopy: root.querySelector<HTMLElement>('.credit-copy')!,
    beginButton: root.querySelector<HTMLButtonElement>('.begin-button')!,
    restButton: root.querySelector<HTMLButtonElement>('.rest-button')!,
    warningCard: root.querySelector<HTMLElement>('.warning-card')!,
    messageCopy: root.querySelector<HTMLElement>('.message-copy')!,
  };

  function persist() {
    state.currentPetType = getStage(state.level);
    saveState(state);
  }

  function setMessage(message: string) {
    state.message = message;
    render();
    persist();
  }

  function ensureWorker() {
    if (worker) return worker;

    worker = new Worker(new URL('./flowPetTimer.worker.ts', import.meta.url));
    worker.onmessage = (event: MessageEvent<WorkerEvent>) => {
      const data = event.data;
      state.remainingMs = data.remainingMs;
      state.sessionDurationMs = data.durationMs;

      if (state.timerStatus === 'running' && document.visibilityState === 'visible' && document.hasFocus()) {
        const now = Date.now();
        state.creditedFocusMs += Math.max(0, now - lastCreditTickAt);
        lastCreditTickAt = now;
      }

      if (data.type === 'complete') {
        completeTimer(data.mode);
        return;
      }

      render();
      persist();
    };

    return worker;
  }

  function startTimer(mode: 'focus' | 'break', minutes: number) {
    const durationMs = minutes * minuteMs;
    state.timerStatus = mode === 'focus' ? 'running' : 'break';
    state.sessionDurationMs = durationMs;
    state.remainingMs = durationMs;
    state.creditedFocusMs = 0;
    state.lastWarningAt = null;
    hiddenStartedAt = null;
    lastCreditTickAt = Date.now();
    ensureWorker().postMessage({ type: 'start', durationMs, mode });
    setMessage(mode === 'focus' ? 'Quest started. Stay with the portal to earn every spark of EXP.' : 'Break started. Feed and play are available while your pet rests.');
  }

  function completeTimer(mode: 'focus' | 'break') {
    if (mode === 'focus') {
      const awardedXp = Math.max(10, Math.round(state.creditedFocusMs / minuteMs) * 10);
      state.timerStatus = 'completed';
      state.remainingMs = 0;
      state.health = clamp(state.health + 8, 0, 100);
      grantXp(awardedXp, `Quest complete. Your pet gained ${awardedXp} EXP.`);
      burstParticles('level');
    } else {
      state.timerStatus = 'idle';
      state.remainingMs = state.selectedMinutes * minuteMs;
      state.message = 'Break complete. The portal is ready for another quest.';
    }

    render();
    persist();
  }

  function grantXp(amount: number, message: string) {
    const previousStage = getStage(state.level);
    state.xp += amount;
    let leveled = false;

    while (state.xp >= getXpToNext(state.level)) {
      state.xp -= getXpToNext(state.level);
      state.level += 1;
      leveled = true;
      openEligibleChests();
    }

    const nextStage = getStage(state.level);
    if (leveled) {
      message = `${message} Level ${state.level} reached.`;
      burstParticles(previousStage !== nextStage ? 'evolution' : 'level');
    }

    if (previousStage !== nextStage) {
      message = `${message} Evolution unlocked: ${getStageName(nextStage)}.`;
    }

    state.currentPetType = nextStage;
    state.message = message;
    render();
    persist();
  }

  function openEligibleChests() {
    lootMilestones.forEach((level) => {
      if (state.level < level || state.openedMilestones.includes(level)) {
        return;
      }

      const drop = items.find((item) => item.unlockLevel === level);
      state.openedMilestones.push(level);
      if (drop && !state.inventory.includes(drop.id)) {
        state.inventory.push(drop.id);
        state.message = `Loot chest opened at level ${level}: ${drop.name}.`;
      }
    });
  }

  function abandonSession(reason: string) {
    if (state.timerStatus !== 'running') return;

    ensureWorker().postMessage({ type: 'reset' });
    state.timerStatus = 'neglect';
    state.remainingMs = state.selectedMinutes * minuteMs;
    state.health = clamp(state.health - healthPenalty, 0, 100);
    state.xp = Math.max(0, state.xp - xpPenalty);
    state.lastWarningAt = Date.now();
    hiddenStartedAt = null;
    state.message = `${reason} Pet neglect applied: -${healthPenalty} health and -${xpPenalty} EXP.`;
    render();
    persist();
  }

  function handleFocusLoss() {
    if (state.timerStatus !== 'running') return;

    hiddenStartedAt = Date.now();
    state.timerStatus = 'paused';
    state.lastWarningAt = hiddenStartedAt;
    ensureWorker().postMessage({ type: 'pause' });
    state.message = 'Pet Neglect warning: return to the quest before it is abandoned.';
    render();
    persist();
  }

  function handleFocusReturn() {
    if (state.timerStatus !== 'paused' || hiddenStartedAt === null) return;

    const awayMs = Date.now() - hiddenStartedAt;
    if (awayMs >= neglectGraceMs) {
      abandonSession('The focus quest was abandoned while the portal was unattended.');
      return;
    }

    state.timerStatus = 'running';
    hiddenStartedAt = null;
    lastCreditTickAt = Date.now();
    ensureWorker().postMessage({ type: 'resume' });
    state.message = 'Quest resumed. EXP gain is active again.';
    render();
    persist();
  }

  function tapPet() {
    if (state.currentPetType === 'egg' && state.timerStatus !== 'running') {
      grantXp(5, 'The egg shimmered. Tiny bond EXP gained.');
      return;
    }

    burstParticles('level');
    setMessage(state.timerStatus === 'running' ? 'Your pet is guarding the quest with you.' : 'Your pet seems delighted.');
  }

  function doBreakAction(kind: 'feed' | 'play') {
    if (state.timerStatus !== 'break' && state.timerStatus !== 'completed') {
      setMessage('Feed and Play are only available during rest periods.');
      return;
    }

    state.health = clamp(state.health + (kind === 'feed' ? 10 : 5), 0, 100);
    grantXp(kind === 'feed' ? 8 : 12, kind === 'feed' ? 'Snack shared. Health and EXP rose.' : 'Play break complete. Your pet gained cheerful EXP.');
  }

  function equipItem(itemId: string) {
    const item = items.find((candidate) => candidate.id === itemId);
    if (!item) return;

    if (state.equipped[item.slot] === item.id) {
      delete state.equipped[item.slot];
      setMessage(`${item.name} unequipped.`);
      return;
    }

    state.equipped[item.slot] = item.id;
    setMessage(`${item.name} equipped.`);
  }

  function burstParticles(type: 'level' | 'evolution') {
    selectors.particleField.classList.remove('is-bursting', 'is-evolving');
    window.requestAnimationFrame(() => {
      selectors.particleField.classList.add(type === 'evolution' ? 'is-evolving' : 'is-bursting');
    });

    if (particleTimer) window.clearTimeout(particleTimer);
    particleTimer = window.setTimeout(() => {
      selectors.particleField.classList.remove('is-bursting', 'is-evolving');
    }, 1300);
  }

  function renderPet() {
    const stage = state.currentPetType;
    const equippedItems = Object.values(state.equipped)
      .map((itemId) => items.find((item) => item.id === itemId))
      .filter((item): item is CostumeItem => Boolean(item));

    selectors.petComposite.className = `pet-composite pet-${stage}`;
    selectors.petComposite.innerHTML = `
      <div class="pet-shadow"></div>
      <div class="pet-base" aria-hidden="true">
        <span class="pet-aura"></span>
        <span class="pet-body"></span>
        <span class="pet-face"></span>
        <span class="pet-wing pet-wing-left"></span>
        <span class="pet-wing pet-wing-right"></span>
        <span class="pet-tail"></span>
      </div>
      ${equippedItems
        .map((item) => `<span class="costume costume-${item.slot} costume-${item.id}" title="${item.name}">${item.glyph}</span>`)
        .join('')}
    `;
  }

  function renderInventory() {
    if (state.inventory.length === 0) {
      selectors.inventoryList.innerHTML = '<p class="empty-inventory">Loot chests unlock at levels 3, 7, and 12.</p>';
      return;
    }

    selectors.inventoryList.innerHTML = '';
    state.inventory.forEach((itemId) => {
      const item = items.find((candidate) => candidate.id === itemId);
      if (!item) return;

      const button = make('button', 'inventory-item');
      const equipped = state.equipped[item.slot] === item.id;
      button.type = 'button';
      button.classList.toggle('is-equipped', equipped);
      button.innerHTML = `
        <span class="item-glyph">${item.glyph}</span>
        <span>
          <strong>${item.name}</strong>
          <small>${equipped ? 'Equipped' : item.description}</small>
        </span>
      `;
      button.addEventListener('click', () => equipItem(item.id));
      selectors.inventoryList.append(button);
    });
  }

  function render() {
    state.currentPetType = getStage(state.level);
    const xpToNext = getXpToNext(state.level);
    const xpPercent = clamp((state.xp / xpToNext) * 100, 0, 100);
    const healthPercent = clamp(state.health, 0, 100);
    const actionEnabled = state.timerStatus === 'break' || state.timerStatus === 'completed';

    selectors.stageName.textContent = getStageName(state.currentPetType);
    selectors.levelBadge.textContent = `LV ${state.level}`;
    selectors.xpCopy.textContent = `${state.xp} / ${xpToNext} EXP`;
    selectors.xpFill.style.width = `${xpPercent}%`;
    selectors.healthCopy.textContent = `${state.health}%`;
    selectors.healthFill.style.width = `${healthPercent}%`;
    selectors.feedButton.disabled = !actionEnabled;
    selectors.playButton.disabled = !actionEnabled;
    selectors.countdown.textContent = formatTime(state.remainingMs);
    selectors.creditCopy.textContent = `${Math.floor(state.creditedFocusMs / minuteMs)} focused minutes banked`;
    selectors.statusCopy.textContent = {
      idle: 'Portal idle',
      running: 'Quest active',
      paused: 'Pet Neglect warning',
      break: 'Resting',
      completed: 'Quest complete',
      neglect: 'Neglect recovered',
    }[state.timerStatus];
    selectors.warningCard.textContent =
      state.timerStatus === 'paused'
        ? 'Leaving the tab pauses EXP gain. Return within 30 seconds to avoid a neglect penalty.'
        : state.timerStatus === 'neglect'
          ? 'Your pet needs a clean quest to rebuild trust.'
          : 'Stay in the portal during focus quests to keep EXP active.';
    selectors.messageCopy.textContent = state.message;
    selectors.lootCopy.textContent = `Next chest: level ${lootMilestones.find((level) => level > state.level) ?? 'max'}`;
    selectors.beginButton.textContent = state.timerStatus === 'running' ? 'Quest Running' : 'Begin Quest';
    selectors.beginButton.disabled = state.timerStatus === 'running' || state.timerStatus === 'paused' || state.timerStatus === 'break';
    selectors.restButton.textContent = state.timerStatus === 'break' ? 'Resting' : 'Rest';
    selectors.restButton.disabled = state.timerStatus === 'running' || state.timerStatus === 'paused' || state.timerStatus === 'break';
    selectors.lengthButtons.forEach((button) => {
      const active = Number(button.dataset.minutes) === state.selectedMinutes;
      button.classList.toggle('is-active', active);
      button.disabled = state.timerStatus === 'running' || state.timerStatus === 'paused' || state.timerStatus === 'break';
    });

    renderPet();
    renderInventory();
  }

  selectors.lengthButtons.forEach((button) => {
    button.addEventListener('click', () => {
      state.selectedMinutes = Number(button.dataset.minutes) === 50 ? 50 : 25;
      state.sessionDurationMs = state.selectedMinutes * minuteMs;
      state.remainingMs = state.sessionDurationMs;
      setMessage(`${state.selectedMinutes}-minute quest selected.`);
    });
  });

  selectors.beginButton.addEventListener('click', () => startTimer('focus', state.selectedMinutes));
  selectors.restButton.addEventListener('click', () => startTimer('break', 5));
  selectors.feedButton.addEventListener('click', () => doBreakAction('feed'));
  selectors.playButton.addEventListener('click', () => doBreakAction('play'));
  selectors.petStage.addEventListener('click', tapPet);
  selectors.petStage.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      tapPet();
    }
  });

  const visibilityListener = () => {
    if (document.visibilityState === 'hidden') {
      handleFocusLoss();
    } else {
      handleFocusReturn();
    }
  };
  const blurListener = () => handleFocusLoss();
  const focusListener = () => handleFocusReturn();

  document.addEventListener('visibilitychange', visibilityListener);
  window.addEventListener('blur', blurListener);
  window.addEventListener('focus', focusListener);

  if (state.timerStatus === 'paused') {
    state.timerStatus = 'idle';
    state.remainingMs = state.selectedMinutes * minuteMs;
    state.creditedFocusMs = 0;
    state.message = 'A previous quest was paused. Begin a fresh quest when you are ready.';
  }

  openEligibleChests();
  render();
  persist();

  return () => {
    document.removeEventListener('visibilitychange', visibilityListener);
    window.removeEventListener('blur', blurListener);
    window.removeEventListener('focus', focusListener);
    if (particleTimer) window.clearTimeout(particleTimer);
    worker?.terminate();
  };
}
